'use client';

import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { getAppointmentLocation } from './_services/get-appointment-location.service';
import { AppointmentTodayPill } from './_components/appointment-items/appointment-today-pill.component';

import { CardAppointmentHeader } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_primitives/card-appointment-header.component';
import { DetailsAccordion } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_primitives/details-accordion.component';
import { EditableField } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_primitives/editable-field.component';
import { GuideCodeField } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_primitives/guide-code-field.component';
import { LabeledField } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_primitives/labeled-field.component';
import { LocationBlock } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_primitives/location-block.component';
import { AppointmentCardActions } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/card-appointment-actions.component';
import { CardAppointmentLabExpanded } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/card-appointment-lab-expanded.component';
import { CardAppointmentProps } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/card-appointment.component';
import { Card } from '@/components/ui/card.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { dateFormatter } from '@/utils/formatters/date';
import { formatClientName } from '@/utils/formatters/format-to-title-case';
import { partnerDisplayName } from '@/utils/formatters/appointment-entity';
import { getPartnerProfilePath } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_utils/appointment-entity-path';

export function CardAppointmentLab({ appointment, defaultOpen, onAppointmentUpdate }: CardAppointmentProps) {
  const patientName = formatClientName(appointment.patient);
  const location = getAppointmentLocation(appointment);
  const partnerName = partnerDisplayName(appointment.partner);

  const startDate = dateFormatter(appointment.date, 'date');
  const startHour = dateFormatter(appointment.date, 'HH:mm');
  const endHour = appointment.end ? dateFormatter(appointment.end, 'HH:mm') : null;
  const formattedDate = endHour
    ? `${startDate}, das ${startHour} às ${endHour} (horário de Brasília), por ordem de chegada`
    : `${startDate} às ${startHour} (horário de Brasília), por ordem de chegada`;

  const examCount = appointment.items.length;
  const examSummary = `${examCount} ${examCount === 1 ? 'exame' : 'exames'} (Consulte a lista abaixo)`;

  return (
    <AppointmentCardActions appointment={appointment} onAppointmentUpdate={onAppointmentUpdate}>
      {({ onEditDate, onEditPatient }) => (
        <Card
          data-testid={DataTestIdEnum.APPOINTMENT_CARD_LAB}
          className="flex flex-col gap-4 rounded-2xl p-4 w-full"
        >
          <div className="flex flex-col gap-4">
            <AppointmentTodayPill date={appointment.date} />

            <CardAppointmentHeader
              avatarSrc={appointment.partner?.avatar}
              avatarAlt={partnerName}
              avatarType={EntityTypeEnum.LABORATORY_COLLECT_POINT}
              name={partnerName}
              href={getPartnerProfilePath(appointment)}
            />

            <hr className="border-t border-gray-200 w-full" />

            <LabeledField label="Exames">
              <Typography>{examSummary}</Typography>
            </LabeledField>

            <GuideCodeField appointment={appointment} />

            <EditableField
              label="Data e hora"
              value={formattedDate}
              onEdit={onEditDate}
              editAriaLabel="Editar data"
              editTestId={`${DataTestIdEnum.APPOINTMENT_CARD_EDIT_DATE}-${appointment.id}`}
            />

            <EditableField
              label="Atendimento para"
              value={patientName}
              onEdit={onEditPatient}
              editAriaLabel="Editar paciente"
              editTestId={`${DataTestIdEnum.APPOINTMENT_CARD_EDIT_PATIENT}-${appointment.id}`}
            />

            <LocationBlock {...location} />
          </div>

          <DetailsAccordion defaultOpen={defaultOpen}>
            <CardAppointmentLabExpanded
              appointment={appointment}
              onEditPatient={onEditPatient}
              onEditDate={onEditDate}
            />
          </DetailsAccordion>
        </Card>
      )}
    </AppointmentCardActions>
  );
}
